import React, { useState, useEffect } from "react";
import { Box, Typography, Paper, Button } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { ResponsivePie } from "@nivo/pie";
import StatBox from "../../components/StatBox";
import Header from "../../components/Header";

// Color palette for the pie charts
const pieColors = [
    "#FF5733", // Reddish
    "#33FF57", // Greenish
    "#3357FF", // Blueish
    "#F39C12", // Orange
    "#8E44AD", // Purple
];

const DashboardInvoluntary = () => {
    const [records, setRecords] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pageSize, setPageSize] = useState(10);
    // State for filtering the table based on pie slice clicks.
    const [selectedType, setSelectedType] = useState(null);
    const [selectedWorkqueue, setSelectedWorkqueue] = useState(null);

    // Fetch data from the API endpoint for lo_HR_Separation_tracking
    useEffect(() => {
        console.log("Fetching Separation Tracking data...");
        fetch("https://azuresql.swancreate.com/api/swa/vw_HR_Separation_tracking")
            .then((res) => res.json())
            .then((data) => {
                // Group data by ProcessID and keep only the record with the latest CreatedOn date.
                const grouped = data.reduce((acc, record) => {
                    if (
                        !acc[record.ProcessID] ||
                        new Date(record.CreatedOn) > new Date(acc[record.ProcessID].CreatedOn)
                    ) {
                        acc[record.ProcessID] = record;
                    }
                    return acc;
                }, {});
                const uniqueRecords = Object.values(grouped);
                console.log("Unique records by ProcessID:", uniqueRecords);
                setRecords(uniqueRecords);
                setLoading(false);
            })
            .catch((err) => {
                console.error("Error fetching data:", err);
                setLoading(false);
            });
    }, []);

    // Total count (unique processes)
    const totalRecords = records.length;

    // Compute filtered records for the table based on the current pie slice selection.
    const filteredRecords = records.filter((rec) => {
        if (selectedType && rec["Type"] !== selectedType) return false;
        if (selectedWorkqueue && rec["Workqueue"] !== selectedWorkqueue) return false;
        return true;
    });

    // Split records by Type for the pie charts.
    const involuntaryRecords = records.filter((rec) => rec["Type"] === "Involuntary");
    const voluntaryRecords = records.filter((rec) => rec["Type"] === "Voluntary");

    // Group Involuntary records by Workqueue.
    const involuntaryWorkqueueCounts = involuntaryRecords.reduce((acc, rec) => {
        const wq = rec["Workqueue"] || "Unknown";
        acc[wq] = (acc[wq] || 0) + 1;
        return acc;
    }, {});
    const involuntaryPieData = Object.entries(involuntaryWorkqueueCounts).map(
        ([key, value], index) => ({
            id: key,
            label: key,
            value,
            color: pieColors[index % pieColors.length],
        })
    );

    // Group Voluntary records by Workqueue.
    const voluntaryWorkqueueCounts = voluntaryRecords.reduce((acc, rec) => {
        const wq = rec["Workqueue"] || "Unknown";
        acc[wq] = (acc[wq] || 0) + 1;
        return acc;
    }, {});
    const voluntaryPieData = Object.entries(voluntaryWorkqueueCounts).map(
        ([key, value], index) => ({
            id: key,
            label: key,
            value,
            color: pieColors[index % pieColors.length],
        })
    );

    // Define columns for the DataGrid.
    const columns = [
        { field: "ProcessID", headerName: "Process ID", flex: 1 },
        { field: "EmployeeID", headerName: "Employee ID", flex: 1 },
        { field: "FirstName", headerName: "First Name", flex: 1 },
        { field: "LastName", headerName: "Last Name", flex: 1 },
        { field: "Workqueue", headerName: "Workqueue", flex: 1 },
        { field: "Type", headerName: "Type", flex: 1 },
    ];

    // Map filtered records to DataGrid rows.
    const rows = filteredRecords.map((rec, index) => ({
        id: index, // Alternatively, you could use rec.ProcessID if unique.
        ...rec,
    }));

    // CSV Download handler: downloads CSV from the current filtered records.
    const downloadCSV = () => {
        const csvHeaders = columns.map((col) => col.headerName).join(",");
        const csvRows = filteredRecords.map((row) =>
            columns
                .map((col) => {
                    let cell = row[col.field] || "";
                    if (typeof cell === "string") {
                        cell = cell.replace(/"/g, '""');
                        return `"${cell}"`;
                    }
                    return cell;
                })
                .join(",")
        );
        const csvContent = [csvHeaders, ...csvRows].join("\n");
        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "separation_tracking.csv");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // Reset filters handler.
    const resetFilters = () => {
        setSelectedType(null);
        setSelectedWorkqueue(null);
    };

    return (
        <Box m="20px">
            <Header title="CCPS HR" subtitle="Track Separation Requests" />

            {/* Stat Box */}
            <Box display="grid" gridTemplateColumns="repeat(12, 1fr)" gap="20px" my="20px">
                <Box gridColumn="span 12">
                    <StatBox title={totalRecords} subtitle="Total Separation Records" />
                </Box>
            </Box>

            {/* Pie Charts: Left for Involuntary, Right for Voluntary */}
            <Box display="flex" gap="20px" mb="20px">
                {/* Involuntary by Workqueue */}
                <Paper sx={{ flex: 1, height: 450, p: 2, backgroundColor: "#2c3e50" }}>
                    <Typography variant="h6" color="#fff" mb={2}>
                        Involuntary by Workqueue
                    </Typography>
                    <Box height="400px">
                        <ResponsivePie
                            data={involuntaryPieData}
                            margin={{ top: 40, right: 120, bottom: 80, left: 80 }}
                            innerRadius={0}
                            padAngle={0.7}
                            cornerRadius={5}
                            colors={involuntaryPieData.map((d) => d.color)}
                            borderWidth={1}
                            borderColor={{ from: "color", modifiers: [["darker", 0.3]] }}
                            radialLabelsSkipAngle={10}
                            radialLabelsTextColor="#fff"
                            radialLabelsLinkColor={{ from: "color" }}
                            sliceLabelsSkipAngle={10}
                            sliceLabelsTextColor="#fff"
                            legends={[
                                {
                                    anchor: "right",
                                    direction: "column",
                                    translateX: 20,
                                    translateY: 0,
                                    itemWidth: 100,
                                    itemHeight: 18,
                                    itemTextColor: "#fff",
                                    symbolSize: 18,
                                    symbolShape: "circle",
                                    effects: [
                                        {
                                            on: "hover",
                                            style: { itemTextColor: "#fff" },
                                        },
                                    ],
                                },
                            ]}
                            theme={{
                                legends: { text: { fontSize: 16 } },
                                labels: { text: { fontSize: 16, fill: "#fff" } },
                            }}
                            onClick={(datum) => {
                                // Toggle filter: if already selected, clear; else set to Involuntary and workqueue equals slice's label.
                                if (selectedType === "Involuntary" && selectedWorkqueue === datum.id) {
                                    setSelectedType(null);
                                    setSelectedWorkqueue(null);
                                } else {
                                    setSelectedType("Involuntary");
                                    setSelectedWorkqueue(datum.id);
                                }
                            }}
                        />
                    </Box>
                </Paper>

                {/* Voluntary by Workqueue */}
                <Paper sx={{ flex: 1, height: 450, p: 2, backgroundColor: "#2c3e50" }}>
                    <Typography variant="h6" color="#fff" mb={2}>
                        Voluntary by Workqueue
                    </Typography>
                    <Box height="400px">
                        <ResponsivePie
                            data={voluntaryPieData}
                            margin={{ top: 40, right: 120, bottom: 80, left: 80 }}
                            innerRadius={0}
                            padAngle={0.7}
                            cornerRadius={5}
                            colors={voluntaryPieData.map((d) => d.color)}
                            borderWidth={1}
                            borderColor={{ from: "color", modifiers: [["darker", 0.3]] }}
                            radialLabelsSkipAngle={10}
                            radialLabelsTextColor="#fff"
                            radialLabelsLinkColor={{ from: "color" }}
                            sliceLabelsSkipAngle={10}
                            sliceLabelsTextColor="#fff"
                            legends={[
                                {
                                    anchor: "right",
                                    direction: "column",
                                    translateX: 20,
                                    translateY: 0,
                                    itemWidth: 100,
                                    itemHeight: 18,
                                    itemTextColor: "#fff",
                                    symbolSize: 18,
                                    symbolShape: "circle",
                                    effects: [
                                        {
                                            on: "hover",
                                            style: { itemTextColor: "#fff" },
                                        },
                                    ],
                                },
                            ]}
                            theme={{
                                legends: { text: { fontSize: 16 } },
                                labels: { text: { fontSize: 16, fill: "#fff" } },
                            }}
                            onClick={(datum) => {
                                // Toggle filter for voluntary: if already selected, clear; else set to Voluntary and workqueue equals slice's label.
                                if (selectedType === "Voluntary" && selectedWorkqueue === datum.id) {
                                    setSelectedType(null);
                                    setSelectedWorkqueue(null);
                                } else {
                                    setSelectedType("Voluntary");
                                    setSelectedWorkqueue(datum.id);
                                }
                            }}
                        />
                    </Box>
                </Paper>
            </Box>

            {/* Active Filters & Control Buttons */}
            <Box display="flex" gap={2} mb="20px">
                {(selectedType || selectedWorkqueue) && (
                    <Typography variant="subtitle1" color="textSecondary">
                        {selectedType ? `Filter: ${selectedType} | Workqueue: ${selectedWorkqueue}` : ""}
                    </Typography>
                )}
                <Button variant="contained" color="primary" onClick={resetFilters}>
                    Reset Filters
                </Button>
                <Button variant="contained" color="success" onClick={downloadCSV}>
                    Download CSV
                </Button>
            </Box>

            {/* Data Table */}
            <Box
                m="20px 0"
                height="600px"
                sx={{
                    "& .MuiDataGrid-root": { border: "none" },
                    "& .MuiDataGrid-row:nth-of-type(odd)": { backgroundColor: "#34495e" },
                    "& .MuiDataGrid-row:nth-of-type(even)": { backgroundColor: "#2c3e50" },
                    "& .MuiDataGrid-columnHeaders": { backgroundColor: "#2c3e50", borderBottom: "none" },
                    "& .MuiCheckbox-root.Mui-checked": { color: "#fff" },
                }}
            >
                <DataGrid
                    rows={rows}
                    columns={columns}
                    loading={loading}
                    pageSize={pageSize}
                    onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
                    rowsPerPageOptions={[5, 10, 25]}
                    density="compact"
                    checkboxSelection
                    disableSelectionOnClick
                />
            </Box>
        </Box>
    );
};

export default DashboardInvoluntary;
